<template>
  <div class="ds-table-wrap">
    <table class="ds-table">
      <thead>
        <tr>
          <th
            v-for="field in fields"
            :key="field.key"
            :style="field.width ? { width: field.width } : null"
          >
            <!-- labelSrOnly keeps the header readable to a screen reader without
                 showing it, for columns like row actions. -->
            <span :class="{ 'ds-sr-only': field.labelSrOnly }">{{ field.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="busy">
          <td
            :colspan="fields.length"
            class="ds-table-state"
          >
            <ds-spinner />
            {{ busyText }}
          </td>
        </tr>
        <tr v-else-if="!items.length">
          <td
            :colspan="fields.length"
            class="ds-table-state"
          >
            {{ emptyText }}
          </td>
        </tr>
        <template v-else>
          <template v-for="(item, index) in items">
            <!-- An item flagged `divider` groups the rows under it: a spanning header
                 row rather than a real row, so it can't be mistaken for data. -->
            <tr
              v-if="item.divider"
              :key="rowKey(item, index)"
              class="ds-table-divider"
            >
              <th
                :colspan="fields.length"
                scope="colgroup"
              >
                <slot
                  name="divider"
                  :item="item"
                >{{ item.label }}</slot>
              </th>
            </tr>
            <tr
              v-else
              :key="rowKey(item, index)"
            >
              <td
                v-for="field in fields"
                :key="field.key"
              >
                <!-- cell(<key>) mirrors b-table's slot naming. -->
                <slot
                  :name="`cell(${field.key})`"
                  :item="item"
                  :value="item[field.key]"
                ><template v-if="isBlank(item[field.key])">
                  <span aria-hidden="true">-</span><span class="ds-sr-only">Not set</span>
                </template><template v-else>
                  {{ item[field.key] }}
                </template></slot>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import DsSpinner from './DsSpinner.vue'

export default {
  name: 'DsTable',
  components: {
    DsSpinner,
  },
  props: {
    // [{ key, label, width?, labelSrOnly? }] sets the columns in order.
    fields: {
      type: Array,
      required: true,
    },
    // Rows. An entry with `divider: true` renders as a spanning group header
    // showing its `label` (or the `divider` slot) instead of a data row.
    items: {
      type: Array,
      default: () => [],
    },
    busy: {
      type: Boolean,
      default: false,
    },
    busyText: {
      type: String,
      default: 'Loading…',
    },
    emptyText: {
      type: String,
      default: 'Nothing to show yet.',
    },
  },
  methods: {
    // Namespaced so an id and an index can't collide, and id 0 doesn't fall through.
    rowKey(item, index) {
      return item.id != null ? `id-${item.id}` : `i-${index}`
    },
    isBlank(value) {
      return value === null || value === undefined || value === ''
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

// Wide tables scroll inside the card.
.ds-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.ds-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  th,
  td {
    padding: 13px 26px;
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    @include ds-eyebrow;

    background: $c-bg;
    border-bottom: 1px solid $c-border;
    font-size: 10.5px;
  }

  td {
    color: $c-text;
    border-bottom: 1px solid $c-border-light;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: $c-bg;
  }
}

// A quiet band, not a second header: it separates without competing with thead.
.ds-table-divider th {
  padding: 9px 26px;
  background: $c-bg;
  border-top: 1px solid $c-border-light;
  border-bottom: 1px solid $c-border-light;
  color: $c-text-sec;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

// The first divider sits directly under thead, which already has a bottom border.
.ds-table-divider:first-child th {
  border-top: none;
}

.ds-sr-only {
  @include ds-sr-only;
}

.ds-table-state {
  color: $c-text-sec;
  font-style: italic;
  text-align: center !important;
  padding: 28px 26px !important;

  &:hover { background: none; }
}

.ds-table-state .ds-spinner {
  margin-right: 7px;
}
</style>
